import React from "react";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import GenericFormDialog, { Field } from "../../components/GenericFormDialog";
import PageHeader from "../../components/PageHeader";
import {
  listNotes,
  Note,
} from "../../services/newNotesService";

const Notes: React.FC = () => {
  const [rows, setRows] = React.useState<Note[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [formValues, setFormValues] = React.useState<any>({});

  // Set default date filter to today and tomorrow
  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const d = new Date();
    const start = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };

  const formFields: Field[] = [
    {
      name: "type",
      label: "Note Type",
      type: "dropdown",
      required: true,
      options: [
        { label: "Credit Note", value: "CREDIT_NOTE" },
        { label: "Debit Note", value: "DEBIT_NOTE" },
      ],
    },
    {
      name: "invoiceId",
      label: "Invoice ID",
      type: "number",
      required: true,
    },
    {
      name: "amount",
      label: "Amount",
      type: "number",
      required: true,
    },
    {
      name: "reason",
      label: "Reason",
    },
  ];

 React.useEffect(() => {
  const fetchNotes = async () => {
    setLoading(true);
    try {
      const params: any = {};

      if (dateRange && !Array.isArray(dateRange)) {
        const fromDate = buildDateBoundary(dateRange.start, false);
        const toDate = buildDateBoundary(dateRange.end, true);
        if (fromDate) params.from = fromDate.toISOString();
        if (toDate) params.to = toDate.toISOString();
      }

      const data = await listNotes(params);
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  fetchNotes();
}, [refetchTrigger, dateRange]);


  return (
    <Box sx={{ width: "100%", maxWidth: "90vw" }}>
      <PageHeader title="Notes & Adjustments" sx={{ mt: { xs: 2, sm: 3 } }} />

    <SearchHeaderContainer
  search={search}
  setSearch={setSearch}
  dateRangeConfig={{
    value: dateRange ?? undefined,
    onChange: (v: any) => setDateRange(v),
    fullWidth: false,
  }}
 
/>


      <CustomTable<Note>
        filters={{ page: 0, size: 10, search }}
        clientSidePagination
        loading={loading}
        fields={[
          { key: "type", header: "Type" },
          { key: "invoiceId", header: "Invoice" },
          { key: "amount", header: "Amount" },
          { key: "status", header: "Status" },
          { key: "createdAt", header: "Date" },
        ]}
        fetchFunc={async (params?: any) => {
          const q = (params && params.search) || search || "";
          const normalized = q.toLowerCase().trim();

          const filtered = rows.filter((r) => {
            if (!normalized) return true;

            const haystack = [
              r.type,
              r.invoiceId,
              r.amount,
              r.status,
              r.createdAt,
            ]
              .join(" ")
              .toLowerCase();

            return haystack.includes(normalized);
          });

          return {
            content: filtered,
            number: 0,
            size: 10,
            totalElements: filtered.length,
            totalPages: 1,
          };
        }}
      />

    
    </Box>
  );
};

export default Notes;
